import { supabase } from '../lib/supabase'
import type { Session, User } from '@supabase/supabase-js'
import {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  User as AppUser
} from '../types/auth'

class SupabaseAuthService {
  private session: Session | null = null
  private user: User | null = null
  private refreshPromise: Promise<void> | null = null
  private initializePromise: Promise<void> | null = null

  constructor() {
    this.initializePromise = this.initializeAuth()
  }

  private async initializeAuth() {
    try {
      // Get initial session from Supabase (handles persistence)
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error) {
        console.error('Error getting initial session:', error)
        return
      }

      this.session = session
      this.user = session?.user || null

      // Listen for auth changes
      supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('Auth state changed:', event, session)
        
        this.session = session
        this.user = session?.user || null

        // Handle different auth events
        switch (event) {
          case 'SIGNED_IN':
            console.log('User signed in')
            break
          case 'SIGNED_OUT':
            console.log('User signed out')
            this.clearTokens()
            break
          case 'TOKEN_REFRESHED':
            console.log('Token refreshed')
            break
          case 'USER_UPDATED':
            console.log('User updated')
            break
        }
      })

      console.log('Auth initialized with session:', !!session)
    } catch (error) {
      console.error('Error initializing auth:', error)
    }
  }

  // Ensure initialization is complete before any auth operations
  private async ensureInitialized() {
    if (this.initializePromise) {
      await this.initializePromise
    }
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    await this.ensureInitialized()

    try {
      // Call backend login endpoint for validation and user data
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Login failed')
      }

      const authResponse = await response.json()

      // Set the session in Supabase client for persistence and auto-refresh
      const { data, error } = await supabase.auth.setSession({
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token
      })

      if (error) {
        console.error('Error setting Supabase session:', error)
        throw new Error('Failed to establish session')
      }

      // Update local state
      this.session = data.session
      this.user = data.user

      return {
        accessToken: authResponse.access_token,
        refreshToken: authResponse.refresh_token,
        expiresIn: authResponse.expires_in,
        tokenType: authResponse.token_type,
        user: authResponse.user
      }
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    await this.ensureInitialized()

    try {
      const url = `${import.meta.env.VITE_API_BASE_URL}/auth/register`
      console.log('SupabaseAuthService: Sending registration data:', userData)
      console.log('SupabaseAuthService: Request URL:', url)
      console.log('SupabaseAuthService: VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL)
      
      // Call backend register endpoint for validation and user creation
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('SupabaseAuthService: Registration failed with status:', response.status)
        console.error('SupabaseAuthService: Error response:', errorData)
        throw new Error(errorData.detail || 'Registration failed')
      }

      const authResponse = await response.json()

      // Set the session in Supabase client for persistence and auto-refresh
      const { data, error } = await supabase.auth.setSession({
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token
      })

      if (error) {
        console.error('Error setting Supabase session:', error)
        throw new Error('Failed to establish session')
      }

      // Update local state
      this.session = data.session
      this.user = data.user

      return {
        accessToken: authResponse.access_token,
        refreshToken: authResponse.refresh_token,
        expiresIn: authResponse.expires_in,
        tokenType: authResponse.token_type,
        user: authResponse.user
      }
    } catch (error) {
      console.error('Registration error:', error)
      throw error
    }
  }

  async logout(): Promise<void> {
    await this.ensureInitialized()

    try {
      // Sign out from Supabase (this clears all sessions and tokens)
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.error('Logout error:', error)
      }
      
      // Clear local state
      this.session = null
      this.user = null
      this.clearTokens()
    } catch (error) {
      console.error('Logout error:', error)
      // Even if logout fails, clear local state
      this.session = null
      this.user = null
      this.clearTokens()
    }
  }

  async refreshAccessToken(): Promise<void> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    this.refreshPromise = this._performRefresh()
    
    try {
      await this.refreshPromise
    } finally {
      this.refreshPromise = null
    }
  }

  private async _performRefresh(): Promise<void> {
    try {
      const { data, error } = await supabase.auth.refreshSession()
      
      if (error || !data.session) {
        console.error('Failed to refresh session:', error)
        throw new Error('Failed to refresh session')
      }

      this.session = data.session
      this.user = data.user
      
      console.log('Session refreshed successfully')
    } catch (error) {
      console.error('Refresh error:', error)
      // On refresh failure, logout user
      await this.logout()
      throw error
    }
  }

  async getCurrentUser(): Promise<AppUser | null> {
    await this.ensureInitialized()
    
    if (!this.session || !this.user) {
      return null
    }

    return this._mapSupabaseUserToAppUser(this.user)
  }

  private _mapSupabaseUserToAppUser(user: User): AppUser {
    return {
      id: user.id,
      email: user.email!,
      role: user.user_metadata?.role || 'user',
      isActive: true,
      emailVerified: user.email_confirmed_at !== null,
      createdAt: user.created_at || new Date().toISOString(),
      updatedAt: user.updated_at || new Date().toISOString(),
      lastLogin: user.last_sign_in_at || null,
      preferences: user.user_metadata || {},
      termsAcceptedAt: user.user_metadata?.terms_accepted_at || null,
      privacyAcceptedAt: user.user_metadata?.privacy_accepted_at || null,
      dataProcessingConsent: user.user_metadata?.data_processing_consent || false,
      marketingConsent: user.user_metadata?.marketing_consent || false
    }
  }

  // Token management methods for compatibility
  getAccessToken(): string | null {
    return this.session?.access_token || null
  }

  getRefreshToken(): string | null {
    return this.session?.refresh_token || null
  }

  isAuthenticated(): boolean {
    return !!this.session && !!this.user
  }

  isTokenExpiringSoon(): boolean {
    if (!this.session) return true
    
    const expiresAt = this.session.expires_at
    if (!expiresAt) return true
    
    const now = Math.floor(Date.now() / 1000)
    const timeToExpiry = expiresAt - now
    
    // Refresh if token expires in less than 5 minutes
    return timeToExpiry < 300
  }

  async getUserData(): Promise<AppUser | null> {
    return this.getCurrentUser()
  }

  // Wait for authentication initialization
  async waitForAuth(): Promise<boolean> {
    await this.ensureInitialized()
    return this.isAuthenticated()
  }

  // Get session info
  getSession(): Session | null {
    return this.session
  }

  // Get raw Supabase user
  getSupabaseUser(): User | null {
    return this.user
  }

  // Check if user email is verified
  isEmailVerified(): boolean {
    return this.user?.email_confirmed_at !== null
  }

  // Handle auth state restoration on app startup
  async restoreAuthState(): Promise<boolean> {
    await this.ensureInitialized()
    
    if (this.session && this.isTokenExpiringSoon()) {
      try {
        await this.refreshAccessToken()
      } catch (error) {
        console.error('Failed to refresh expired token:', error)
        return false
      }
    }
    
    return this.isAuthenticated()
  }

  clearTokens(): void {
    // Clear any additional local storage if needed
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_refresh_token')
    localStorage.removeItem('user_preferences')
  }

  // Password reset methods
  async requestPasswordReset(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    
    if (error) {
      throw new Error(error.message)
    }
  }

  async updatePassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    })
    
    if (error) {
      throw new Error(error.message)
    }
  }

}

// Export singleton instance
export const supabaseAuthService = new SupabaseAuthService()